/**
 * Inbox Hotel filter — independent from global header while on the inbox page.
 * Persists across refresh (sessionStorage); resets to All when leaving inbox and returning.
 */

import { normalizeHotelSelection } from "./inboxFilterState"

export const INBOX_HOTEL_FILTER_KEY = "inbox_hotel_filter_v1"
export const INBOX_HOTEL_FILTER_OVERRIDE_KEY = "inbox_hotel_filter_user_override_v1"
export const INBOX_HOTEL_RESET_ON_RETURN_KEY = "inbox_hotel_reset_on_return_v1"

function storage() {
  try {
    return sessionStorage
  } catch {
    return null
  }
}

/** Call from Layout when SPA navigates away from Community AI Inbox. */
export function markInboxHotelFilterResetOnReturn() {
  const s = storage()
  if (!s) return
  try {
    s.setItem(INBOX_HOTEL_RESET_ON_RETURN_KEY, "1")
  } catch {
    /* ignore */
  }
}

export function peekInboxHotelResetOnReturn() {
  try {
    return sessionStorage.getItem(INBOX_HOTEL_RESET_ON_RETURN_KEY) === "1"
  } catch {
    return false
  }
}

/** True when user left inbox and should see global header hotel again. */
export function consumeInboxHotelResetOnReturn() {
  if (!peekInboxHotelResetOnReturn()) return false
  clearInboxHotelFilter()
  return true
}

function clearResetOnReturnFlag() {
  try {
    sessionStorage.removeItem(INBOX_HOTEL_RESET_ON_RETURN_KEY)
  } catch {
    /* ignore */
  }
}

/** Remove legacy localStorage keys from an earlier implementation. */
function clearLegacyLocalStorage() {
  try {
    localStorage.removeItem(INBOX_HOTEL_FILTER_KEY)
    localStorage.removeItem(INBOX_HOTEL_FILTER_OVERRIDE_KEY)
  } catch {
    /* ignore */
  }
}

export function markInboxHotelFilterUserOverride() {
  const s = storage()
  if (!s) return
  try {
    s.setItem(INBOX_HOTEL_FILTER_OVERRIDE_KEY, "1")
  } catch {
    /* ignore */
  }
}

export function hasInboxHotelFilterUserOverride() {
  const s = storage()
  if (!s) return false
  try {
    return s.getItem(INBOX_HOTEL_FILTER_OVERRIDE_KEY) === "1"
  } catch {
    return false
  }
}

/** Saved inbox hotel names, or `[]` when user chose All Properties. */
export function readInboxHotelFilter() {
  const s = storage()
  if (!s) return null
  try {
    if (hasInboxHotelFilterUserOverride()) {
      const raw = s.getItem(INBOX_HOTEL_FILTER_KEY)
      if (!raw || raw === "all") return []
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return normalizeHotelSelection(parsed)
      } catch {
        /* legacy single hotel name */
      }
      return normalizeHotelSelection(raw)
    }
    const v = s.getItem(INBOX_HOTEL_FILTER_KEY)
    if (!v || String(v).trim() === "" || v === "all") return null
    try {
      const parsed = JSON.parse(v)
      if (Array.isArray(parsed)) return normalizeHotelSelection(parsed)
    } catch {
      /* legacy */
    }
    return normalizeHotelSelection(v)
  } catch {
    return null
  }
}

export function writeInboxHotelFilter(hotelSelection) {
  markInboxHotelFilterUserOverride()
  const s = storage()
  if (!s) return
  try {
    const selected = normalizeHotelSelection(hotelSelection)
    if (selected.length === 0) {
      s.removeItem(INBOX_HOTEL_FILTER_KEY)
    } else {
      s.setItem(INBOX_HOTEL_FILTER_KEY, JSON.stringify(selected))
    }
  } catch {
    /* ignore */
  }
}

export function clearInboxHotelFilter() {
  const s = storage()
  try {
    if (s) {
      s.removeItem(INBOX_HOTEL_FILTER_KEY)
      s.removeItem(INBOX_HOTEL_FILTER_OVERRIDE_KEY)
    }
    clearResetOnReturnFlag()
    clearLegacyLocalStorage()
  } catch {
    /* ignore */
  }
}

export function readGlobalHeaderHotelName() {
  try {
    const raw = localStorage.getItem("selectedClient")
    if (raw) {
      const p = JSON.parse(raw)
      if (p?.name) return p.name
    }
  } catch {
    /* ignore */
  }
  return "all"
}

/** Initial hotel filter: [] = All. Restores session choice when user picked hotels in inbox. */
export function readInitialInboxHotels() {
  clearLegacyLocalStorage()

  if (peekInboxHotelResetOnReturn()) {
    return []
  }

  const stored = readInboxHotelFilter()
  if (stored != null) return stored

  return []
}

/** @deprecated Use readInitialInboxHotels — kept for callers expecting a string. */
export function readInitialInboxHotel() {
  const hotels = readInitialInboxHotels()
  if (hotels.length === 0) return "all"
  if (hotels.length === 1) return hotels[0]
  return hotels
}
